import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { Gauge, X } from 'lucide-react';

// Days of week: Mon=1 .. Sun=7
const WEEK_DAYS = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 7, label: 'Вс' },
];

// Working-window modes
const MODE_OFF = 'off';
const MODE_DAILY = 'daily';
const MODE_DAILY_BLACKOUT = 'daily_blackout';
const MODE_INTERVAL = 'interval';

const MODE_OPTIONS = [
  { value: MODE_OFF, label: 'Круглосуточно (без ограничений)' },
  { value: MODE_DAILY, label: 'Ежедневное окно' },
  { value: MODE_DAILY_BLACKOUT, label: 'Ежедневное окно с исключением дней' },
  { value: MODE_INTERVAL, label: 'Циклы: работа / пауза' },
];

// Time units for interval cycles (value stored in seconds on the backend)
const UNIT_SECONDS = 'seconds';
const UNIT_MINUTES = 'minutes';
const UNIT_HOURS = 'hours';

const UNIT_OPTIONS = [
  { value: UNIT_SECONDS, label: 'секунды', factor: 1 },
  { value: UNIT_MINUTES, label: 'минуты', factor: 60 },
  { value: UNIT_HOURS, label: 'часы', factor: 3600 },
];

const UNIT_FACTOR = {
  [UNIT_SECONDS]: 1,
  [UNIT_MINUTES]: 60,
  [UNIT_HOURS]: 3600,
};

// Pick a human-friendly unit for a raw seconds value
const secondsToValueUnit = (totalSeconds) => {
  const n = Number(totalSeconds);
  if (!Number.isFinite(n) || n < 0) return { value: '', unit: UNIT_SECONDS };
  if (n !== 0 && n % 3600 === 0) return { value: String(n / 3600), unit: UNIT_HOURS };
  if (n !== 0 && n % 60 === 0) return { value: String(n / 60), unit: UNIT_MINUTES };
  return { value: String(n), unit: UNIT_SECONDS };
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  fontSize: '14px',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: 'var(--text-primary)',
  marginBottom: '4px',
};

const ScanControlSettings = ({ project, onClose }) => {
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rps, setRps] = useState('');
  const [maxConcurrent, setMaxConcurrent] = useState('');
  const [mode, setMode] = useState(MODE_OFF);

  // daily fields
  const [tz, setTz] = useState('Europe/Moscow');
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('18:00');
  const [days, setDays] = useState([]); // allow-list, empty = all days
  const [blackoutDays, setBlackoutDays] = useState([]);

  // interval fields (value + unit; canonical storage is seconds)
  const [workValue, setWorkValue] = useState('60');
  const [workUnit, setWorkUnit] = useState(UNIT_MINUTES);
  const [pauseValue, setPauseValue] = useState('30');
  const [pauseUnit, setPauseUnit] = useState(UNIT_MINUTES);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await projectsAPI.getProject(project._id);
        if (cancelled) return;
        const doc = response.data?.project || response.data || {};

        if (doc.rps !== undefined && doc.rps !== null) setRps(String(doc.rps));
        if (doc.max_concurrent_scans !== undefined && doc.max_concurrent_scans !== null) {
          setMaxConcurrent(String(doc.max_concurrent_scans));
        }

        const wh = doc.working_hours;
        if (!wh || wh.enabled === false) {
          setMode(MODE_OFF);
        } else if (wh.mode === 'interval') {
          setMode(MODE_INTERVAL);
          // Prefer canonical seconds; fall back to legacy minutes.
          if (wh.work_seconds !== undefined && wh.work_seconds !== null) {
            const w = secondsToValueUnit(wh.work_seconds);
            setWorkValue(w.value);
            setWorkUnit(w.unit);
          } else if (wh.work_minutes !== undefined && wh.work_minutes !== null) {
            setWorkValue(String(wh.work_minutes));
            setWorkUnit(UNIT_MINUTES);
          }
          if (wh.pause_seconds !== undefined && wh.pause_seconds !== null) {
            const p = secondsToValueUnit(wh.pause_seconds);
            setPauseValue(p.value);
            setPauseUnit(p.unit);
          } else if (wh.pause_minutes !== undefined && wh.pause_minutes !== null) {
            setPauseValue(String(wh.pause_minutes));
            setPauseUnit(UNIT_MINUTES);
          }
        } else {
          // daily
          if (wh.tz) setTz(wh.tz);
          if (wh.start) setStart(wh.start);
          if (wh.end) setEnd(wh.end);
          if (Array.isArray(wh.days)) setDays(wh.days);
          if (Array.isArray(wh.blackout_days)) setBlackoutDays(wh.blackout_days);
          setMode(Array.isArray(wh.blackout_days) && wh.blackout_days.length > 0 ? MODE_DAILY_BLACKOUT : MODE_DAILY);
        }
      } catch (error) {
        if (!cancelled) {
          showError(error.response?.data?.message || 'Не удалось загрузить настройки сканирования');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id]);

  const toggleDay = (list, setList, value) => {
    if (list.includes(value)) {
      setList(list.filter((d) => d !== value));
    } else {
      setList([...list, value].sort((a, b) => a - b));
    }
  };

  const handleSave = async () => {
    const body = {};

    const rpsNum = parseInt(rps, 10);
    if (!Number.isNaN(rpsNum)) body.rps = rpsNum;

    const kNum = parseInt(maxConcurrent, 10);
    if (!Number.isNaN(kNum)) body.max_concurrent_scans = kNum;

    if (mode === MODE_OFF) {
      body.working_hours = { enabled: false };
    } else if (mode === MODE_DAILY || mode === MODE_DAILY_BLACKOUT) {
      const wh = {
        enabled: true,
        mode: 'daily',
        tz,
        start,
        end,
        days,
      };
      if (mode === MODE_DAILY_BLACKOUT) {
        wh.blackout_days = blackoutDays;
      }
      body.working_hours = wh;
    } else if (mode === MODE_INTERVAL) {
      const workNum = Number(workValue);
      const pauseNum = Number(pauseValue);
      if (!Number.isFinite(workNum) || workNum <= 0) {
        showError('Значение «Работа» должно быть положительным числом');
        return;
      }
      if (!Number.isFinite(pauseNum) || pauseNum < 0) {
        showError('Значение «Пауза» должно быть числом не меньше 0');
        return;
      }
      body.working_hours = {
        enabled: true,
        mode: 'interval',
        work_seconds: Math.round(workNum * UNIT_FACTOR[workUnit]),
        pause_seconds: Math.round(pauseNum * UNIT_FACTOR[pauseUnit]),
        anchor_ts: Math.floor(Date.now() / 1000),
      };
    }

    try {
      setSaving(true);
      await projectsAPI.updateProject(project._id, body);
      showSuccess('Настройки сканирования сохранены');
      onClose();
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось сохранить настройки сканирования');
    } finally {
      setSaving(false);
    }
  };

  const renderDayPicker = (list, setList) => (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {WEEK_DAYS.map((d) => {
        const active = list.includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            onClick={() => toggleDay(list, setList, d.value)}
            style={{
              padding: '6px 10px',
              border: `1px solid ${active ? '#10b981' : 'var(--border-color)'}`,
              borderRadius: '6px',
              backgroundColor: active ? '#10b981' : 'transparent',
              color: active ? 'white' : 'var(--text-primary)',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '560px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Gauge size={20} />
            Настройки сканирования
          </h2>
          <button
            onClick={onClose}
            style={{ padding: '4px', border: 'none', backgroundColor: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}
            title="Закрыть"
          >
            <X size={20} />
          </button>
        </div>

        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
          Проект: <strong style={{ color: 'var(--text-primary)' }}>{project.name}</strong>
        </p>

        {loading ? (
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Загрузка...</p>
        ) : (
          <>
            {/* RPS */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Лимит запросов в секунду (RPS)</label>
              <input
                type="number"
                min="1"
                value={rps}
                onChange={(e) => setRps(e.target.value)}
                placeholder="например, 50"
                style={inputStyle}
              />
            </div>

            {/* Max concurrent scans */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Макс. одновременных сканов (K)</label>
              <input
                type="number"
                min="1"
                value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
                placeholder="например, 3"
                style={inputStyle}
              />
            </div>

            {/* Mode */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Режим рабочего окна</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} style={inputStyle}>
                {MODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Daily fields */}
            {(mode === MODE_DAILY || mode === MODE_DAILY_BLACKOUT) && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '6px',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Часовой пояс</label>
                  <input type="text" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="Europe/Moscow" style={inputStyle} />
                </div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Начало</label>
                    <input type="time" value={start} onChange={(e) => setStart(e.target.value)} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Конец</label>
                    <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: mode === MODE_DAILY_BLACKOUT ? '12px' : 0 }}>
                  <label style={labelStyle}>Дни недели</label>
                  {renderDayPicker(days, setDays)}
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                    Пусто — все дни недели.
                  </p>
                </div>
                {mode === MODE_DAILY_BLACKOUT && (
                  <div>
                    <label style={labelStyle}>Исключить дни</label>
                    {renderDayPicker(blackoutDays, setBlackoutDays)}
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      В эти дни сканирование принудительно останавливается.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Interval fields */}
            {mode === MODE_INTERVAL && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: 'var(--bg-tertiary)',
                  borderRadius: '6px',
                  display: 'flex',
                  gap: '12px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Работа</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      min="1"
                      value={workValue}
                      onChange={(e) => setWorkValue(e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <select value={workUnit} onChange={(e) => setWorkUnit(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                      {UNIT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Пауза</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      min="0"
                      value={pauseValue}
                      onChange={(e) => setPauseValue(e.target.value)}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <select value={pauseUnit} onChange={(e) => setPauseUnit(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
                      {UNIT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={onClose}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScanControlSettings;
